import { WalkthroughStep } from "../../../common/components/walkthroughV3/useWalkthrough";

export function groupAdminsSteps(options: { isSuperAdmin?: boolean; isBizboxAdmin?: boolean } = {}): WalkthroughStep[] {
  const { isSuperAdmin = false, isBizboxAdmin = false } = options;
  const showManageAccess = isSuperAdmin || isBizboxAdmin;

  return [
  {
    title: {
      en: "Group Admins",
      tl: "Group Admins",
    },
    caption: {
      en: isSuperAdmin
        ? "This module lets you view and manage co-group admins for your organization. You can invite new admins, edit their profiles, lock or unlock accounts, and manage user access."
        : "This module lets you view and manage co-group admins for your organization. You can invite new admins, edit their profiles, and lock or unlock accounts.",
      tl: isSuperAdmin
        ? "Ang module na ito ay nagbibigay-daan sa iyo na tingnan at pamahalaan ang mga co-group admin ng iyong organisasyon. Maaari kang mag-imbita ng mga bagong admin, mag-edit ng kanilang profile, mag-lock o mag-unlock ng mga account, at pamahalaan ang access ng user."
        : "Ang module na ito ay nagbibigay-daan sa iyo na tingnan at pamahalaan ang mga co-group admin ng iyong organisasyon. Maaari kang mag-imbita ng mga bagong admin, mag-edit ng kanilang profile, at mag-lock o mag-unlock ng mga account.",
    },
    highlights: [{ id: "user-menu-group-admins" }],
    group: "Overview",
    captionPosition: "right",
  },
  {
    title: {
      en: "Group Selector",
      tl: "Selector ng Grupo",
    },
    caption: {
      en: "Use the **Group** dropdown at the top to switch between client groups. The admin list will update to show only the admins belonging to the selected group.",
      tl: "Gamitin ang **Group** dropdown sa itaas para lumipat sa pagitan ng mga grupo ng kliyente. Ia-update ang listahan ng admin para ipakita lamang ang mga admin na kabilang sa napiling grupo.",
    },
    highlights: [{ id: "wt-filter" }],
    group: "Overview",
    isButtonOrSearchBar: true,
    captionPosition: "bottom",
  },
  {
    title: {
      en: "Admin List",
      tl: "Listahan ng Admin",
    },
    caption: {
      en: "This table shows the Group Admin and all co-group admins in your organization. There is only one Group Admin per organization, while co-group admins can be multiple.",
      tl: "Ipinapakita ng talahanayan na ito ang Group Admin at lahat ng co-group admin sa iyong organisasyon. Isang Group Admin lamang ang maaaring mayroon bawat organisasyon, habang maaaring maraming co-group admin.",
    },
    highlights: [{ id: "group-admin-data-table" }],
    group: "Page Tools",
    showTableData: true,
    subCaption: [
      {
        title: { en: "Columns:", tl: "Mga Kolum:" },
        subTitle: [
          { subTitle: { en: "**Name** — Full name of the admin.", tl: "**Pangalan** — Buong pangalan ng admin." } },
          { subTitle: { en: "**Phone** — Contact number on file.", tl: "**Telepono** — Numero ng kontak na nakatala." } },
          { subTitle: { en: "**Email** — Email address with verification status.", tl: "**Email** — Email address na may katayuan ng verification." } },
          { subTitle: { en: "**Last Login** — When they last accessed the system.", tl: "**Huling Login** — Kailan sila huling nag-access sa sistema." } },
          { subTitle: { en: "**Action** — ⋮ menu to edit, lock, or manage access.", tl: "**Aksyon** — ⋮ menu para mag-edit, mag-lock, o pamahalaan ang access." } },
        ],
      },
    ],
  },
  {
    title: {
      en: "Search",
      tl: "Paghahanap",
    },
    caption: {
      en: "Type an admin's name or email in the search bar to filter the list. Press **Enter** or click the magnifying glass to apply.",
      tl: "I-type ang pangalan o email ng admin sa search bar para i-filter ang listahan. Pindutin ang **Enter** o i-click ang magnifying glass para ilapat.",
    },
    highlights: [{ id: "group-admin-search-bar" }],
    group: "Page Tools",
    isButtonOrSearchBar: true,
  },
  {
    title: {
      en: "Invite Co-Admin",
      tl: "Mag-imbita ng Co-Admin",
    },
    caption: {
      en: 'Click **"Invite User"** to register a new co-group admin. A step-by-step dialog will guide you through searching for or creating the new account.',
      tl: 'I-click ang **"Invite User"** para magrehistro ng bagong co-group admin. Isang hakbang-hakbang na dialog ang gagabay sa iyo sa paghahanap o paglikha ng bagong account.',
    },
    highlights: [{ id: "group-admin-invite-button" }],
    group: "Invite Flow",
    isButtonOrSearchBar: true,
    subCaption: [
      {
        title: {
          en: "Reminder:",
          tl: "Paalala:",
        },
        subTitle: [
          {
            subTitle: {
              en: "Only users with the invite privilege can register new co-group admins.",
              tl: "Tanging ang mga user na may pribilehiyo sa pag-imbita lamang ang maaaring magrehistro ng mga bagong co-group admin.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Find the User",
      tl: "Hanapin ang User",
    },
    caption: {
      en: "Select the lookup type (Email or Full Name) and enter the person's details to check if they already have an account in the system. Select them from the results, or proceed to create a new one.",
      tl: "Piliin ang uri ng paghahanap (Email o Full Name) at ilagay ang detalye ng tao para malaman kung mayroon na silang account sa sistema. Piliin sila mula sa mga resulta, o magpatuloy para lumikha ng bago.",
    },
    highlights: [{ id: "invite-user-search-modal" }],
    group: "Invite Flow",
    captionPosition: "left",
    skipAutoAdvance: true,
  },
  {
    title: {
      en: "Fill in Details",
      tl: "Punan ang mga Detalye",
    },
    caption: {
      en: "Fill in the new co-admin's personal information here. Click **Save** when done — login credentials will be sent to their email address automatically.",
      tl: "Punan ang personal na impormasyon ng bagong co-admin dito. I-click ang **Save** kapag tapos na — awtomatikong ipapadala ang mga kredensyal sa pag-login sa kanilang email address.",
    },
    highlights: [{ id: "group-admin-user-dialog" }],
    group: "Invite Flow",
    captionPosition: "left",
    skipAutoAdvance: true,
    delayAfterNext: 150,
    fieldHighlights: [
      { phrase: "first name", id: "first-name-profile-textfield" },
      { phrase: "first name", id: "lastname-profile-textfield" },
      { phrase: "email address —", id: "email-profile-textfield" },
      { phrase: "civil status", id: "civilstatus-profile-textfield" },
      { phrase: "civil status", id: "gender-profile-textfield" },
    ],
    subCaption: [
      {
        title: { en: "Fields to fill:", tl: "Mga field na pupunan:" },
        subTitle: [
          { subTitle: { en: "**First Name, Last Name** — Co-admin's full legal name.", tl: "**First Name, Last Name** — Buong legal na pangalan ng co-admin." } },
          { subTitle: { en: "**Email Address** — Login credentials will be sent here.", tl: "**Email Address** — Dito ipapadala ang mga kredensyal sa pag-login." } },
          { subTitle: { en: "**Civil Status & Gender** — Required personal information.", tl: "**Civil Status & Gender** — Kinakailangang personal na impormasyon." } },
        ],
      },
    ],
  },
  {
    title: {
      en: "User Actions",
      tl: "Mga Aksyon ng User",
    },
    caption: {
      en: "Click the **⋮ menu** on any row to manage that co-admin.",
      tl: "I-click ang **⋮ menu** sa anumang row para pamahalaan ang co-admin na iyon.",
    },
    highlights: [{ id: "user-actions-menu" }],
    group: "User Management",
    captionPosition: "left",
    isButtonOrSearchBar: true,
    skipAutoAdvance: true,
    subCaption: [
      {
        title: {
          en: "Available Actions:",
          tl: "Mga Available na Aksyon:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Edit** — Update the co-admin's profile information.",
              tl: "**Edit** — I-update ang impormasyon ng profile ng co-admin.",
            },
          },
          {
            subTitle: {
              en: "**Lock / Unlock User** — Restrict or restore the user's login access.",
              tl: "**Lock / Unlock User** — Paghigpitan o ibalik ang access sa pag-login ng user.",
            },
          },
          ...(showManageAccess
            ? [
                {
                  subTitle: {
                    en: "**Manage Access** — Control the user's BizBox system access and permissions.",
                    tl: "**Manage Access** — Kontrolin ang BizBox system access at mga pahintulot ng user.",
                  },
                },
              ]
            : []),
          ...(showManageAccess
            ? [
                {
                  subTitle: {
                    en: "**Delete User** — Permanently remove a Co-Group Admin from the organization.",
                    tl: "**Delete User** — Permanenteng alisin ang isang Co-Group Admin mula sa organisasyon.",
                  },
                },
              ]
            : []),
          ...(showManageAccess
            ? [
                {
                  subTitle: {
                    en: "**Resend Email** — Resend login credentials to co-admins who haven't logged in yet.",
                    tl: "**Resend Email** — Muling ipadala ang mga kredensyal sa pag-login sa mga co-admin na hindi pa naka-login.",
                  },
                },
              ]
            : []),
        ],
      },
    ],
  },
  {
    title: {
      en: "Walkthrough Icon",
      tl: "Icon ng Walkthrough",
    },
    caption: {
      en: "Tip: If you accidentally close this walkthrough, just click the **💡 icon** anytime to start it again from the beginning.",
      tl: "Tip: Kung aksidenteng nasara ang walkthrough, i-click lang ang **💡 icon** anumang oras upang simulan ito muli mula sa simula.",
    },
    highlights: [{ id: "group-admin-walkthrough-start-button" }],
    group: "Tips",
    captionPosition: "left",
    isButtonOrSearchBar: true,
  },
  ];
}

export function bizboxUsersSteps(options: { isSuperAdmin?: boolean; isBizboxAdmin?: boolean } = {}): WalkthroughStep[] {
  const { isSuperAdmin = false, isBizboxAdmin = false } = options;
  const showManageAccess = isSuperAdmin || isBizboxAdmin;

  return [
  {
    title: {
      en: "BizBox Users",
      tl: "BizBox Users",
    },
    caption: {
      en: "This module lets you view and manage BizBox users for your organization. You can invite new users, edit their profiles, lock or unlock accounts, and manage user access.",
      tl: "Ang module na ito ay nagbibigay-daan sa iyo na tingnan at pamahalaan ang mga BizBox user ng iyong organisasyon. Maaari kang mag-imbita ng mga bagong user, mag-edit ng kanilang profile, mag-lock o mag-unlock ng mga account, at pamahalaan ang user access.",
    },
    highlights: [{ id: "user-menu-bizbox-users" }],
    group: "Overview",
    captionPosition: "right",
  },
  {
    title: {
      en: "BizBox User List",
      tl: "Listahan ng BizBox User",
    },
    caption: {
      en: "This table shows all BizBox users in your organization. Each row displays the user's name, phone, email, last login, department, and an action menu.",
      tl: "Ipinapakita ng talahanayan na ito ang lahat ng BizBox user sa iyong organisasyon. Ipinapakita ng bawat row ang pangalan, telepono, email, huling login, departamento, at action menu ng user.",
    },
    highlights: [{ id: "group-admin-data-table" }],
    group: "Page Tools",
    showTableData: true,
    subCaption: [
      {
        title: { en: "Columns:", tl: "Mga Kolum:" },
        subTitle: [
          { subTitle: { en: "**Name** — Full name of the BizBox user.", tl: "**Pangalan** — Buong pangalan ng BizBox user." } },
          { subTitle: { en: "**Phone** — Contact number on file.", tl: "**Telepono** — Numero ng kontak na nakatala." } },
          { subTitle: { en: "**Email** — Email address with verification status.", tl: "**Email** — Email address na may katayuan ng verification." } },
          { subTitle: { en: "**Last Login** — When they last accessed the system.", tl: "**Huling Login** — Kailan sila huling nag-access sa sistema." } },
          { subTitle: { en: "**Department** — The department the user belongs to.", tl: "**Departamento** — Ang departamento kung saan kabilang ang user." } },
          { subTitle: { en: "**Action** — ⋮ menu to edit, lock, or manage access.", tl: "**Aksyon** — ⋮ menu para mag-edit, mag-lock, o pamahalaan ang access." } },
        ],
      },
    ],
  },
  {
    title: {
      en: "Search",
      tl: "Paghahanap",
    },
    caption: {
      en: "Type a user's name or email in the search bar to filter the list. Press **Enter** or click the magnifying glass to apply.",
      tl: "I-type ang pangalan o email ng user sa search bar para i-filter ang listahan. Pindutin ang **Enter** o i-click ang magnifying glass para ilapat.",
    },
    highlights: [{ id: "group-admin-search-bar" }],
    group: "Page Tools",
    isButtonOrSearchBar: true,
  },
  {
    title: {
      en: "Invite User",
      tl: "Mag-imbita ng User",
    },
    caption: {
      en: 'Click **"Invite User"** to register a new BizBox user. A step-by-step dialog will guide you through searching for or creating the new account.',
      tl: 'I-click ang **"Invite User"** para magrehistro ng bagong BizBox user. Isang hakbang-hakbang na dialog ang gagabay sa iyo sa paghahanap o paglikha ng bagong account.',
    },
    highlights: [{ id: "group-admin-invite-button" }],
    group: "Invite Flow",
    isButtonOrSearchBar: true,
    subCaption: [
      {
        title: {
          en: "Reminder:",
          tl: "Paalala:",
        },
        subTitle: [
          {
            subTitle: {
              en: "Only users with the invite privilege can register new BizBox users.",
              tl: "Tanging ang mga user na may pribilehiyo sa pag-imbita lamang ang maaaring magrehistro ng mga bagong BizBox user.",
            },
          },
        ],
      },
    ],
  },
  {
    title: {
      en: "Find the User",
      tl: "Hanapin ang User",
    },
    caption: {
      en: "Select the lookup type (Email or Full Name) and enter the person's details to check if they already have an account in the system. Select them from the results, or proceed to create a new one.",
      tl: "Piliin ang uri ng paghahanap (Email o Full Name) at ilagay ang detalye ng tao para malaman kung mayroon na silang account sa sistema. Piliin sila mula sa mga resulta, o magpatuloy para lumikha ng bago.",
    },
    highlights: [{ id: "invite-user-search-modal" }],
    group: "Invite Flow",
    captionPosition: "left",
    skipAutoAdvance: true,
  },
  {
    title: {
      en: "Fill in Details",
      tl: "Punan ang mga Detalye",
    },
    caption: {
      en: "Fill in the new user's personal information here. Click **Save** when done — login credentials will be sent to their email address automatically.",
      tl: "Punan ang personal na impormasyon ng bagong user dito. I-click ang **Save** kapag tapos na — awtomatikong ipapadala ang mga kredensyal sa pag-login sa kanilang email address.",
    },
    highlights: [{ id: "group-admin-user-dialog" }],
    group: "Invite Flow",
    captionPosition: "left",
    skipAutoAdvance: true,
    delayAfterNext: 150,
    fieldHighlights: [
      { phrase: "first name", id: "first-name-profile-textfield" },
      { phrase: "first name", id: "lastname-profile-textfield" },
      { phrase: "email address —", id: "email-profile-textfield" },
      { phrase: "civil status", id: "civilstatus-profile-textfield" },
      { phrase: "civil status", id: "gender-profile-textfield" },
      { phrase: "department", id: "department-profile-textfield" },
    ],
    subCaption: [
      {
        title: { en: "Fields to fill:", tl: "Mga field na pupunan:" },
        subTitle: [
          { subTitle: { en: "**First Name, Last Name** — User's full legal name.", tl: "**First Name, Last Name** — Buong legal na pangalan ng user." } },
          { subTitle: { en: "**Email Address** — Login credentials will be sent here.", tl: "**Email Address** — Dito ipapadala ang mga kredensyal sa pag-login." } },
          { subTitle: { en: "**Civil Status & Gender** — Required personal information.", tl: "**Civil Status & Gender** — Kinakailangang personal na impormasyon." } },
          { subTitle: { en: "**Department** — Assign the user to their department.", tl: "**Department** — I-assign ang user sa kanilang departamento." } },
        ],
      },
    ],
  },
  {
    title: {
      en: "User Actions",
      tl: "Mga Aksyon ng User",
    },
    caption: {
      en: "Click the **⋮ menu** on any row to manage that user.",
      tl: "I-click ang **⋮ menu** sa anumang row para pamahalaan ang user na iyon.",
    },
    highlights: [{ id: "user-actions-menu" }],
    group: "User Management",
    captionPosition: "left",
    isButtonOrSearchBar: true,
    skipAutoAdvance: true,
    subCaption: [
      {
        title: {
          en: "Available Actions:",
          tl: "Mga Available na Aksyon:",
        },
        subTitle: [
          {
            subTitle: {
              en: "**Edit** — Update the user's profile information.",
              tl: "**Edit** — I-update ang impormasyon ng profile ng user.",
            },
          },
          {
            subTitle: {
              en: "**Lock / Unlock User** — Restrict or restore the user's login access.",
              tl: "**Lock / Unlock User** — Paghigpitan o ibalik ang access sa pag-login ng user.",
            },
          },
          ...(showManageAccess
            ? [
                {
                  subTitle: {
                    en: "**Manage Access** — Control the user's BizBox system access and permissions.",
                    tl: "**Manage Access** — Kontrolin ang BizBox system access at mga pahintulot ng user.",
                  },
                },
                {
                  subTitle: {
                    en: "**Delete User** — Permanently remove a BizBox user from the organization.",
                    tl: "**Delete User** — Permanenteng alisin ang isang BizBox user mula sa organisasyon.",
                  },
                },
                {
                  subTitle: {
                    en: "**Resend Email** — Resend login credentials to users who haven't logged in yet.",
                    tl: "**Resend Email** — Muling ipadala ang mga kredensyal sa pag-login sa mga user na hindi pa naka-login.",
                  },
                },
              ]
            : []),
        ],
      },
    ],
  },
  {
    title: {
      en: "Walkthrough Icon",
      tl: "Icon ng Walkthrough",
    },
    caption: {
      en: "Tip: If you accidentally close this walkthrough, just click the **💡 icon** anytime to start it again from the beginning.",
      tl: "Tip: Kung aksidenteng nasara ang walkthrough, i-click lang ang **💡 icon** anumang oras upang simulan ito muli mula sa simula.",
    },
    highlights: [{ id: "bizbox-users-walkthrough-start-button" }],
    group: "Tips",
    captionPosition: "left",
    isButtonOrSearchBar: true,
  },
  ];
}
